// Notification API Service - FCM Token Management + Backend Notification Service
// Integrated with Spring Boot Ticket Service FCM endpoints and Notification endpoints
import { apiPost, apiGet, apiDelete, apiPut } from '../services/apiClient';
import { extractUserInfo } from '../utils/jwtDecoder';
import { isBackendPersistedNotificationId } from '../utils/notificationNavigation';

/**
 * Get authentication token from storage
 * @returns {string|null} JWT token or null
 */
const getAuthToken = () => {
  return sessionStorage.getItem("token") || sessionStorage.getItem("authToken");
};

/**
 * Extract user ID from JWT token
 * @returns {number|null} User ID or null
 */
const getUserId = () => {
  const authToken = getAuthToken();
  if (!authToken) {
    return null;
  }
  const userInfo = extractUserInfo(authToken);
  return userInfo?.userId || null;
};

/**
 * Parse ResponseBean from backend
 * @param {object} response - Backend response
 * @returns {object} Parsed response data
 */
const parseResponse = (response) => {
  // Backend returns ResponseBean structure: { status, message, data, success, timestamp }
  if (response && typeof response === 'object') {
    // If response has data property, return it
    if (response.data !== undefined) {
      return response.data;
    }
    // If response is already the data, return it
    return response;
  }
  return response;
};

// Cache for getActiveFCMTokens - ensures only one API call per user per session
let tokensCache = {
  userId: null,
  data: null,
  promise: null
};

/**
 * Clear tokens cache (useful on logout or user change)
 */
export const clearTokensCache = () => {
  tokensCache = {
    userId: null,
    data: null,
    promise: null
  };
};

/**
 * Register/Save FCM token with backend
 * POST /gateway/api/ticket/fcm/savetoken
 * 
 * @param {string} fcmToken - Firebase Cloud Messaging token
 * @param {boolean} isActive - Whether token is active (default: true)
 * @returns {Promise<object>} API response with token data
 */
export const registerFCMToken = async (fcmToken, isActive = true) => {
  if (!fcmToken) {
    throw new Error("FCM token is required");
  }

  const authToken = getAuthToken();
  if (!authToken) {
    throw new Error("User not authenticated");
  }

  const userId = getUserId();
  if (!userId) {
    throw new Error("User ID not found in token");
  }

  // Prepare request body matching FcmTokenRequest DTO
  const requestBody = {
    userId: userId,
    token: fcmToken,
    isActive: isActive
  };

  try {
    // Backend endpoint: POST /api/ticket/fcm/savetoken
    // Gateway routes to: /gateway/api/ticket/fcm/savetoken
    const response = await apiPost('/gateway/api/ticket/fcm/savetoken', requestBody);
    
    // Parse ResponseBean structure
    const parsedResponse = parseResponse(response);
    
    //console.log("✅ FCM token registered with backend:", parsedResponse);
    
    // Clear cache so next getActiveFCMTokens call fetches fresh data
    clearTokensCache();
    
    return {
      success: response.success !== false,
      data: parsedResponse,
      message: response.message || "FCM token registered successfully"
    };
  } catch (error) {
    console.error("❌ Error registering FCM token:", error);
    throw error;
  }
};

/**
 * Update FCM token in backend (same as register - backend handles update if token exists)
 * POST /gateway/api/ticket/fcm/savetoken
 * 
 * @param {string} fcmToken - Firebase Cloud Messaging token
 * @param {boolean} isActive - Whether token is active (default: true)
 * @returns {Promise<object>} API response
 */
export const updateFCMToken = async (fcmToken, isActive = true) => {
  // Backend saveToken method handles both create and update
  // If token exists for user, it updates; otherwise creates new
  return registerFCMToken(fcmToken, isActive);
};

/**
 * Get all active FCM tokens for current user
 * GET /gateway/api/ticket/fcm/token/{userId}
 * 
 * Cached per user session - only makes API call once per login
 * 
 * @returns {Promise<Array>} List of active FCM tokens
 */
export const getActiveFCMTokens = async () => {
  const authToken = getAuthToken();
  if (!authToken) {
    throw new Error("User not authenticated");
  }

  const userId = getUserId();
  if (!userId) {
    throw new Error("User ID not found in token");
  }

  // Check if we have cached data for this user
  if (tokensCache.userId === userId && tokensCache.data !== null) {
    //console.log("✅ Returning cached FCM tokens for user:", userId);
    return tokensCache.data;
  }

  // Check if there's already a pending request for this user
  if (tokensCache.userId === userId && tokensCache.promise !== null) {
    //console.log("⏳ FCM tokens request already in progress, returning existing promise");
    return tokensCache.promise;
  }

  // Create new request promise
  tokensCache.promise = (async () => {
    try {
      // Backend endpoint: GET /api/ticket/fcm/token/{userId}
      const response = await apiGet(`/gateway/api/ticket/fcm/token/${userId}`);
      
      // Parse ResponseBean structure
      const parsedResponse = parseResponse(response);
      
      // Response data is an array of tokens
      const tokens = Array.isArray(parsedResponse) ? parsedResponse : (parsedResponse?.tokens || []);
      
      // Build result object
      const result = {
        success: response.success !== false,
        data: tokens,
        message: response.message || "Active tokens retrieved successfully"
      };
      
      // Cache the result for this user
      tokensCache.userId = userId;
      tokensCache.data = result;
      
      //console.log("✅ Active FCM tokens retrieved and cached:", tokens);
      return result;
    } catch (error) {
      console.error("❌ Error retrieving FCM tokens:", error);
      // Clear promise on error so it can be retried
      tokensCache.promise = null;
      throw error;
    } finally {
      // Clear promise after completion
      tokensCache.promise = null;
    }
  })();

  return tokensCache.promise;
};

/**
 * Deactivate a specific FCM token
 * DELETE /gateway/api/ticket/fcm/token/{userId}?token={token}
 * 
 * @param {string} fcmToken - FCM token to deactivate (optional, uses current token if not provided)
 * @returns {Promise<object>} API response
 */
export const deactivateFCMToken = async (fcmToken = null) => {
  const authToken = getAuthToken();
  if (!authToken) {
    throw new Error("User not authenticated");
  }

  const userId = getUserId();
  if (!userId) {
    throw new Error("User ID not found in token");
  }

  // If token not provided, try to get from localStorage
  if (!fcmToken) {
    fcmToken = localStorage.getItem("notificationToken");
  }

  if (!fcmToken) {
    throw new Error("FCM token is required");
  }

  try {
    // Backend endpoint: DELETE /api/ticket/fcm/token/{userId}?token={token}
    // Build URL with query parameter
    const endpoint = `/gateway/api/ticket/fcm/token/${userId}?token=${encodeURIComponent(fcmToken)}`;
    const response = await apiDelete(endpoint);
    
    // Parse ResponseBean structure
    const parsedResponse = parseResponse(response);
    
    //console.log("✅ FCM token deactivated:", parsedResponse);
    
    // Clear cache so next getActiveFCMTokens call fetches fresh data
    clearTokensCache();
    
    return {
      success: response.success !== false,
      data: parsedResponse,
      message: response.message || "FCM token deactivated successfully"
    };
  } catch (error) {
    console.error("❌ Error deactivating FCM token:", error);
    throw error;
  }
};

/**
 * Deactivate all FCM tokens for current user
 * DELETE /gateway/api/ticket/fcm/token/{userId}/all
 * 
 * @returns {Promise<object>} API response
 */
export const deactivateAllFCMTokens = async () => {
  const authToken = getAuthToken();
  if (!authToken) {
    throw new Error("User not authenticated");
  }

  const userId = getUserId();
  if (!userId) {
    throw new Error("User ID not found in token");
  }

  try {
    // Backend endpoint: DELETE /api/ticket/fcm/token/{userId}/all
    const response = await apiDelete(`/gateway/api/ticket/fcm/token/${userId}/all`);
    
    // Parse ResponseBean structure
    const parsedResponse = parseResponse(response);
    
    //console.log("✅ All FCM tokens deactivated:", parsedResponse);
    
    // Clear cache so next getActiveFCMTokens call fetches fresh data
    clearTokensCache();
    
    return {
      success: response.success !== false,
      data: parsedResponse,
      message: response.message || "All FCM tokens deactivated successfully"
    };
  } catch (error) {
    console.error("❌ Error deactivating all FCM tokens:", error);
    throw error;
  }
};

/**
 * Unregister/Delete FCM token (alias for deactivateFCMToken for backward compatibility)
 * @param {string} fcmToken - FCM token to unregister (optional)
 * @returns {Promise<object>} API response
 */
export const unregisterFCMToken = async (fcmToken = null) => {
  return deactivateFCMToken(fcmToken);
};

// ============================================
// BACKEND NOTIFICATION API METHODS
// ============================================

/**
 * Get user notifications from backend (paginated)
 * GET /gateway/api/ticket/notifications?page=0&size=30&unreadOnly=false
 * 
 * @param {number} page - Page number (0-indexed, default: 0)
 * @param {number} size - Page size (default: 20)
 * @param {boolean} unreadOnly - If true, return only unread notifications (default: false)
 * @returns {Promise<object>} Paginated notifications response
 */
export const getNotifications = async (page = 0, size = 30, unreadOnly = false) => {
  const authToken = getAuthToken();
  if (!authToken) {
    throw new Error("User not authenticated");
  }

  try {
    const params = {
      page: page.toString(),
      size: size.toString(),
    };
    
    if (unreadOnly) {
      params.unreadOnly = 'true';
    }
    
    const queryString = new URLSearchParams(params).toString();
    const response = await apiGet(`/gateway/api/ticket/notifications?${queryString}`);
    
    // Parse ResponseBean structure
    const parsedResponse = parseResponse(response);
    
    return {
      success: response.success !== false,
      data: parsedResponse,
      message: response.message || "Notifications retrieved successfully"
    };
  } catch (error) {
    console.error("❌ Error fetching notifications:", error);
    throw error;
  }
};

/**
 * Get unread notification count from backend
 * GET /gateway/api/ticket/notifications/unread-count
 * 
 * @returns {Promise<number>} Unread notification count
 */
export const getUnreadCount = async () => {
  const authToken = getAuthToken();
  if (!authToken) {
    throw new Error("User not authenticated");
  }

  try {
    const response = await apiGet('/gateway/api/ticket/notifications/unread-count');
    
    // Parse ResponseBean structure
    const parsedResponse = parseResponse(response);
    
    // Response data is the count number
    const count = typeof parsedResponse === 'number' ? parsedResponse : (parsedResponse?.count || 0);
    
    return {
      success: response.success !== false,
      data: count,
      message: response.message || "Unread count retrieved successfully"
    };
  } catch (error) {
    console.error("❌ Error fetching unread count:", error);
    throw error;
  }
};

/**
 * Mark a notification as read
 * PUT /gateway/api/ticket/notifications/{notificationId}/read
 * 
 * @param {number} notificationId - Notification ID to mark as read
 * @returns {Promise<object>} Updated notification response
 */
export const markNotificationAsRead = async (notificationId) => {
  const authToken = getAuthToken();
  if (!authToken) {
    throw new Error("User not authenticated");
  }

  if (!isBackendPersistedNotificationId(notificationId)) {
    return {
      success: true,
      data: null,
      message: "Skipped mark-read for non-persisted notification id",
      skipped: true,
    };
  }

  try {
    const response = await apiPut(`/gateway/api/ticket/notifications/${notificationId}/read`, {});
    
    // Parse ResponseBean structure
    const parsedResponse = parseResponse(response);
    
    return {
      success: response.success !== false,
      data: parsedResponse,
      message: response.message || "Notification marked as read"
    };
  } catch (error) {
    console.error("❌ Error marking notification as read:", error);
    throw error;
  }
};

/**
 * Mark all notifications as read
 * PUT /gateway/api/ticket/notifications/read-all
 * 
 * @returns {Promise<object>} Response with marked count
 */
/**
 * Soft-dismiss all active notifications (Clear all).
 * PUT /gateway/api/ticket/notifications/dismiss-all
 *
 * @returns {Promise<object>} Response with affectedRows and unreadCount
 */
export const dismissAllNotifications = async () => {
  const authToken = getAuthToken();
  if (!authToken) {
    throw new Error("User not authenticated");
  }

  try {
    const response = await apiPut('/gateway/api/ticket/notifications/dismiss-all', {});

    const parsedResponse = parseResponse(response);

    return {
      success: response.success !== false,
      data: parsedResponse,
      message: response.message || "All notifications dismissed",
    };
  } catch (error) {
    console.error("❌ Error dismissing all notifications:", error);
    throw error;
  }
};

export const markAllNotificationsAsRead = async () => {
  const authToken = getAuthToken();
  if (!authToken) {
    throw new Error("User not authenticated");
  }

  try {
    const response = await apiPut('/gateway/api/ticket/notifications/read-all', {});
    
    // Parse ResponseBean structure
    const parsedResponse = parseResponse(response);
    
    return {
      success: response.success !== false,
      data: parsedResponse,
      message: response.message || "All notifications marked as read"
    };
  } catch (error) {
    console.error("❌ Error marking all notifications as read:", error);
    throw error;
  }
};

/**
 * Delete a notification
 * DELETE /gateway/api/ticket/notifications/{notificationId}
 * 
 * @param {number} notificationId - Notification ID to delete
 * @returns {Promise<object>} API response
 */
export const deleteNotification = async (notificationId) => {
  const authToken = getAuthToken();
  if (!authToken) {
    throw new Error("User not authenticated");
  }

  try {
    const response = await apiDelete(`/gateway/api/ticket/notifications/${notificationId}`);
    
    // Parse ResponseBean structure
    const parsedResponse = parseResponse(response);
    
    return {
      success: response.success !== false,
      data: parsedResponse,
      message: response.message || "Notification deleted successfully"
    };
  } catch (error) {
    console.error("❌ Error deleting notification:", error);
    throw error;
  }
};

/**
 * Sync notification from backend when Firebase receives a notification
 * This fetches the full notification details from backend using notificationId from FCM payload
 * 
 * @param {string|number} notificationId - Notification ID from FCM payload data
 * @returns {Promise<object>} Full notification details from backend
 */
export const syncNotificationFromBackend = async (notificationId) => {
  const authToken = getAuthToken();
  if (!authToken) {
    //console.log("User not authenticated, skipping notification sync");
    return null;
  }

  try {
    // Fetch all notifications and find the one matching the ID
    // Since we don't have a direct GET by ID endpoint, we fetch recent notifications
    const response = await getNotifications(0, 30, false);
    
    if (response.success && response.data?.content) {
      // Find notification by ID
      const notification = response.data.content.find(n => 
        n.notificationId === Number(notificationId) || 
        n.notificationId === notificationId
      );
      
      if (notification) {
        //console.log("✅ Notification synced from backend:", notification);
        return notification;
      }
    }
    
    return null;
  } catch (error) {
    console.warn("⚠️ Failed to sync notification from backend:", error);
    // Don't throw - allow FCM notification to still work
    return null;
  }
};

// Export default service object
const notificationService = {
  // FCM Token Management
  registerFCMToken,
  updateFCMToken,
  unregisterFCMToken,
  getActiveFCMTokens,
  deactivateFCMToken,
  deactivateAllFCMTokens,
  clearTokensCache,
  // Backend Notification API
  getNotifications,
  getUnreadCount,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  dismissAllNotifications,
  deleteNotification,
  syncNotificationFromBackend,
};

export default notificationService;
